/**
 * Export editor state to TMX (Tiled XML) format.
 * Produces the same data as TMJExporter but as XML string.
 */
export function exportTMX(state) {
  const mapWidth = state.get('mapWidth');
  const mapHeight = state.get('mapHeight');
  const tileWidth = state.get('tileWidth');
  const tileHeight = state.get('tileHeight');
  const layers = state.get('layers');
  const tilesets = state.get('tilesets');
  const properties = state.get('properties');
  const tileAnimations = state.get('tileAnimations');
  const tileCollisions = state.get('tileCollisions');
  const orientation = state.get('orientation') || 'orthogonal';
  const renderOrder = state.get('renderOrder') || 'right-down';

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += `<map version="1.10" tiledversion="1.11.0" orientation="${esc(orientation)}" renderorder="${esc(renderOrder)}"`;
  xml += ` width="${mapWidth}" height="${mapHeight}" tilewidth="${tileWidth}" tileheight="${tileHeight}" infinite="0">\n`;

  // Map properties
  if (properties && Object.keys(properties).length > 0) {
    xml += exportPropertiesXML(properties, 1);
  }

  // Tilesets
  for (const ts of tilesets) {
    xml += ` <tileset firstgid="${ts.firstGid}" name="${esc(ts.name)}" tilewidth="${ts.tileWidth}" tileheight="${ts.tileHeight}"`;
    xml += ` tilecount="${ts.tileCount}" columns="${ts.columns}"`;
    if (ts.margin) xml += ` margin="${ts.margin}"`;
    if (ts.spacing) xml += ` spacing="${ts.spacing}"`;

    // Check if this tileset has tile-level data (animations or collisions)
    const tileData = collectTileData(ts, tileAnimations, tileCollisions);
    if (tileData.length > 0) {
      xml += '>\n';
      xml += `  <image source="${esc(normalizeTMXPath(ts))}" width="${ts.imageWidth}" height="${ts.imageHeight}"/>\n`;
      for (const td of tileData) {
        xml += `  <tile id="${td.localId}">\n`;
        if (td.animation) {
          xml += '   <animation>\n';
          for (const frame of td.animation) {
            xml += `    <frame tileid="${frame.tileId}" duration="${frame.duration}"/>\n`;
          }
          xml += '   </animation>\n';
        }
        if (td.collisionShapes) {
          xml += '   <objectgroup draworder="index" id="2">\n';
          let objId = 1;
          for (const shape of td.collisionShapes) {
            if (shape.type === 'rect') {
              xml += `    <object id="${objId++}" x="${shape.x}" y="${shape.y}" width="${shape.width}" height="${shape.height}"/>\n`;
            } else if (shape.type === 'polygon') {
              const pts = shape.points.map(p => `${p.x},${p.y}`).join(' ');
              xml += `    <object id="${objId++}" x="${shape.x || 0}" y="${shape.y || 0}">\n`;
              xml += `     <polygon points="${pts}"/>\n`;
              xml += '    </object>\n';
            }
          }
          xml += '   </objectgroup>\n';
        }
        xml += '  </tile>\n';
      }
      xml += ' </tileset>\n';
    } else {
      xml += '>\n';
      xml += `  <image source="${esc(normalizeTMXPath(ts))}" width="${ts.imageWidth}" height="${ts.imageHeight}"/>\n`;
      xml += ' </tileset>\n';
    }
  }

  // Layers
  layers.forEach((layer, i) => {
    xml += exportLayerXML(layer, mapWidth, mapHeight, i + 1, 1);
  });

  xml += '</map>\n';
  return xml;
}

function exportLayerXML(layer, mapWidth, mapHeight, id, indent) {
  const pad = ' '.repeat(indent);

  if (layer.type === 'objectgroup') {
    let xml = `${pad}<objectgroup id="${id}" name="${esc(layer.name)}" draworder="topdown"`;
    if ((layer.opacity ?? 1) !== 1) xml += ` opacity="${layer.opacity}"`;
    if (layer.visible === false) xml += ' visible="0"';
    xml += '>\n';

    if (layer.properties && Object.keys(layer.properties).length > 0) {
      xml += exportPropertiesXML(layer.properties, indent + 1);
    }

    for (const obj of (layer.objects || [])) {
      xml += `${pad} <object id="${obj.id}"`;
      if (obj.name) xml += ` name="${esc(obj.name)}"`;
      if (obj.type) xml += ` type="${esc(obj.type)}"`;
      if (obj.gid) xml += ` gid="${obj.gid}"`;
      xml += ` x="${obj.x}" y="${obj.y}"`;
      if (obj.width) xml += ` width="${obj.width}"`;
      if (obj.height) xml += ` height="${obj.height}"`;
      if (obj.rotation) xml += ` rotation="${obj.rotation}"`;
      if (obj.visible === false) xml += ' visible="0"';

      const hasChildren = obj.polygon || obj.polyline || obj.point ||
        (obj.properties && Object.keys(obj.properties).length > 0);

      if (hasChildren) {
        xml += '>\n';
        if (obj.properties && Object.keys(obj.properties).length > 0) {
          xml += exportPropertiesXML(obj.properties, indent + 2);
        }
        if (obj.point) {
          xml += `${pad}  <point/>\n`;
        }
        if (obj.polygon) {
          const pts = obj.polygon.map(p => `${p.x},${p.y}`).join(' ');
          xml += `${pad}  <polygon points="${pts}"/>\n`;
        }
        if (obj.polyline) {
          const pts = obj.polyline.map(p => `${p.x},${p.y}`).join(' ');
          xml += `${pad}  <polyline points="${pts}"/>\n`;
        }
        xml += `${pad} </object>\n`;
      } else {
        xml += '/>\n';
      }
    }

    xml += `${pad}</objectgroup>\n`;
    return xml;
  }

  // Tile layer — CSV encoding
  let xml = `${pad}<layer id="${id}" name="${esc(layer.name)}" width="${mapWidth}" height="${mapHeight}"`;
  if ((layer.opacity ?? 1) !== 1) xml += ` opacity="${layer.opacity}"`;
  if (layer.visible === false) xml += ' visible="0"';
  if (layer.offsetX) xml += ` offsetx="${layer.offsetX}"`;
  if (layer.offsetY) xml += ` offsety="${layer.offsetY}"`;
  xml += '>\n';

  if (layer.properties && Object.keys(layer.properties).length > 0) {
    xml += exportPropertiesXML(layer.properties, indent + 1);
  }

  xml += `${pad} <data encoding="csv">\n`;
  const data = layer.data || new Uint32Array(mapWidth * mapHeight);
  const rows = [];
  for (let y = 0; y < mapHeight; y++) {
    const row = [];
    for (let x = 0; x < mapWidth; x++) {
      row.push(data[y * mapWidth + x]);
    }
    rows.push(row.join(','));
  }
  xml += rows.join(',\n');
  xml += '\n';
  xml += `${pad} </data>\n`;
  xml += `${pad}</layer>\n`;
  return xml;
}

function exportPropertiesXML(props, indent) {
  if (!props || Object.keys(props).length === 0) return '';
  const pad = ' '.repeat(indent);
  let xml = `${pad}<properties>\n`;
  for (const [name, val] of Object.entries(props)) {
    let type, value;
    if (typeof val === 'object' && val.type) {
      type = val.type;
      value = val.value;
    } else if (typeof val === 'boolean') {
      type = 'bool';
      value = val;
    } else if (typeof val === 'number') {
      type = 'float';
      value = val;
    } else {
      type = 'string';
      value = val;
    }
    // Skip array-typed properties (like collision arrays) — those are TileForge-specific
    if (Array.isArray(value)) continue;
    xml += `${pad} <property name="${esc(name)}" type="${type}" value="${esc(String(value))}"/>\n`;
  }
  xml += `${pad}</properties>\n`;
  return xml;
}

function collectTileData(ts, tileAnimations, tileCollisions) {
  const tileData = [];
  const localIds = new Set();

  // Collect animation data
  if (tileAnimations) {
    for (const [globalId, frames] of tileAnimations) {
      if (globalId >= ts.firstGid && globalId < ts.firstGid + ts.tileCount) {
        const localId = globalId - ts.firstGid;
        localIds.add(localId);
      }
    }
  }

  // Collect collision data
  if (tileCollisions) {
    for (const [globalId] of tileCollisions) {
      if (globalId >= ts.firstGid && globalId < ts.firstGid + ts.tileCount) {
        const localId = globalId - ts.firstGid;
        localIds.add(localId);
      }
    }
  }

  for (const localId of [...localIds].sort((a, b) => a - b)) {
    const globalId = ts.firstGid + localId;
    const entry = { localId };
    if (tileAnimations && tileAnimations.has(globalId)) {
      entry.animation = tileAnimations.get(globalId);
    }
    if (tileCollisions && tileCollisions.has(globalId)) {
      entry.collisionShapes = tileCollisions.get(globalId);
    }
    tileData.push(entry);
  }

  return tileData;
}

function normalizeTMXPath(ts) {
  const path = ts.image || '';
  if ((path.startsWith('tilesets/') && path.endsWith('/image.png')) || path.startsWith('blob:')) {
    const safeName = (ts.name || 'tileset').replace(/[<>:"/\\|?*]/g, '_');
    return `${safeName}.png`;
  }
  return path;
}

function esc(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
